import "@/styles/dividend-payout-print.css";
/**
 * Ведомости начисления зарплаты (журнал).
 *
 * Главная страница для бухгалтера: список всех ведомостей + кнопка
 * «Закрыть месяц» → создаёт draft-ведомости для всех команд/соло.
 * Клик на ведомость — расчётный лист с правкой цифр, корректировками,
 * утверждением, привязанными выплатами, печатью.
 *
 * Источник правды — backend compute_payroll(user_id, ym). UI ничего
 * не считает в формуле: только показывает snapshot.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { Avatar } from "@/components/prototype/Avatar";
import { useAuth } from "@/auth/AuthContext";
import { PeriodPicker } from "@/components/PeriodPicker";
import { usePeriod } from "@/state/period";
import {
  fetchAccruals, fetchAccrual, closeMonth,
  patchAccrual, approveAccrual, unlockAccrual, deleteAccrual,
  addAdjustment, deleteAdjustment, patchAdjustment, formatRub,
  createPayrollStatement,
  type PayrollAccrualDetail, type PayrollAdjustmentKind,
} from "@/api/finance";

/** Список YYYY-MM, перекрываемых периодом [from..to]. */
function ymsInRange(fromIso: string, toIso: string): string[] {
  if (!fromIso || !toIso) return [];
  const [fy, fm] = fromIso.slice(0, 7).split("-").map(Number);
  const [ty, tm] = toIso.slice(0, 7).split("-").map(Number);
  const out: string[] = [];
  let y = fy, m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return out;
}

const rub = (v: number) => formatRub(v, { mln: false });

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const s = iso.slice(0, 10);
  const [y, m, d] = s.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}
function fmtYM(ym: string): string {
  const RU = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];
  const [y, m] = ym.split("-");
  return `${RU[Number(m) - 1] || m} ${y}`;
}
function currentYm(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const STATUS_LABELS: Record<string, { label: string; bg: string; fg: string }> = {
  draft:    { label: "Черновик",  bg: "rgba(140,140,140,0.18)", fg: "#666" },
  approved: { label: "Утверждена", bg: "rgba(232,200,80,0.30)",  fg: "#a85f12" },
  paid:     { label: "Выплачена",  bg: "rgba(61,122,61,0.18)",   fg: "var(--green)" },
};
const ADJ_LABELS: Record<PayrollAdjustmentKind, string> = {
  bonus: "Премия", penalty: "Штраф", correction: "Корректировка", travel: "Командировка", other: "Прочее",
};

export function FinanceAccrualsPage() {
  const { from, to } = usePeriod();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [openId, setOpenId] = useState<number | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);

  // Используем глобальный PeriodPicker. Бэкенд фильтрует по одному ym;
  // тащим все ведомости за статус и фильтруем по списку месяцев на клиенте.
  const ymsSet = useMemo(() => new Set(ymsInRange(from, to)), [from, to]);
  const periodLabel = useMemo(() => {
    const yms = Array.from(ymsSet).sort();
    if (yms.length === 0) return "";
    if (yms.length === 1) return fmtYM(yms[0]);
    return `${fmtYM(yms[0])} — ${fmtYM(yms[yms.length - 1])}`;
  }, [ymsSet]);

  const listQ = useQuery({
    queryKey: ["payroll-accruals", statusFilter],
    queryFn: () => fetchAccruals({
      status: statusFilter || undefined,
    }),
    staleTime: 30_000,
  });
  const items = useMemo(
    () => (listQ.data ?? []).filter(a => ymsSet.has(a.ym)),
    [listQ.data, ymsSet],
  );
  // Сводка по месяцу (для шапки)
  const sum = useMemo(() => {
    let accrued = 0, paid = 0, balance = 0;
    let drafts = 0, approved = 0, paidN = 0;
    for (const a of items) {
      accrued += a.total_accrued; paid += a.total_paid; balance += a.balance;
      if (a.status === "draft") drafts++;
      else if (a.status === "approved") approved++;
      else if (a.status === "paid") paidN++;
    }
    return { accrued, paid, balance, drafts, approved, paidN };
  }, [items]);

  return (
    <FinanceLayout>
      {/* Строка 1: заголовок + подзаголовок */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Ведомости начисления зарплаты</div>
        <div style={{ fontSize: 12, color: "var(--mute)" }}>
          Источник правды — backend. После «Утвердить» снимок неизменяем.
          Корректировки можно вносить всегда (штраф/премия/прочее).
        </div>
      </div>
      {/* Строка 2: период + статус-фильтры + действие */}
      <div className="filter-bar" style={{ marginBottom: "var(--gap)", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <PeriodPicker/>
        <span style={{ width: 1, height: 18, background: "var(--line)", margin: "0 4px" }}/>
        <button type="button" className={`chip ${!statusFilter ? "active" : ""}`} onClick={() => setStatusFilter("")}>Все</button>
        <button type="button" className={`chip ${statusFilter === "draft" ? "active" : ""}`} onClick={() => setStatusFilter("draft")}>Черновик</button>
        <button type="button" className={`chip ${statusFilter === "approved" ? "active" : ""}`} onClick={() => setStatusFilter("approved")}>Утв.</button>
        <button type="button" className={`chip ${statusFilter === "paid" ? "active" : ""}`} onClick={() => setStatusFilter("paid")}>Выплач.</button>
        <button className="btn btn-primary" style={{ marginLeft: "auto" }} onClick={() => setCloseOpen(true)}>
          <Icon name="plus" size={12}/> Закрыть месяц
        </button>
      </div>

      {/* Сводка по месяцу */}
      <div className="card" style={{ padding: 14, marginBottom: "var(--gap)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <Kpi label={`Ведомостей за ${periodLabel || "период"}`} value={String(items.length)}
               sub={`${sum.drafts} черн · ${sum.approved} утв · ${sum.paidN} выпл`}/>
          <Kpi label="Всего начислено" value={rub(sum.accrued)} color="var(--ink-1)" strong/>
          <Kpi label="Выплачено" value={rub(sum.paid)} color="var(--green)"/>
          <Kpi label="Остаток к выплате" value={rub(sum.balance)}
               color={sum.balance > 0 ? "#a85f12" : "var(--mute)"} strong/>
        </div>
      </div>

      <div className="card">
        {listQ.isLoading ? (
          <div className="muted" style={{ padding: 20 }}>Загрузка…</div>
        ) : listQ.error ? (
          <div className="alert alert-red"><div className="alert-body">{(listQ.error as Error).message}</div></div>
        ) : items.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--mute)" }}>
            За {periodLabel || "выбранный период"} ведомостей нет. Нажмите «Закрыть месяц» — создадутся draft на всех сотрудников.
          </div>
        ) : (
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th>Команда</th>
                  <th style={{ width: 80 }}>Месяц</th>
                  <th style={{ width: 100 }}>Статус</th>
                  <th className="num" style={{ width: 110 }}>Оклад</th>
                  <th className="num" style={{ width: 110 }}>Мотив.</th>
                  <th className="num" style={{ width: 110 }}>Корр.</th>
                  <th className="num" style={{ width: 130 }}>Начислено</th>
                  <th className="num" style={{ width: 130 }}>Выплачено</th>
                  <th className="num" style={{ width: 130 }}>К выплате</th>
                  <th style={{ width: 90 }}/>
                </tr>
              </thead>
              <tbody>
                {items.map(a => (
                  <tr key={a.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(a.id)}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Avatar name={a.user_name || ""} userId={a.user_id} avatarFilename={a.avatar_filename} size={24} color="#4fae4e"/>
                        <span className="strong">{a.user_name || "—"}</span>
                      </div>
                    </td>
                    <td style={{ color: "var(--mute)", fontSize: 12 }}>{a.team_name || "—"}</td>
                    <td>{fmtYM(a.ym)}</td>
                    <td>
                      <span className="badge" style={{
                        background: STATUS_LABELS[a.status]?.bg,
                        color: STATUS_LABELS[a.status]?.fg,
                      }}>{STATUS_LABELS[a.status]?.label || a.status}</span>
                    </td>
                    <td className="num tabular">{rub(a.base_salary)}</td>
                    <td className="num tabular">{rub(a.motivation)}</td>
                    <td className="num tabular" style={{ color: a.manual_adjustments_sum < 0 ? "var(--brick)" : "var(--green)" }}>
                      {a.manual_adjustments_sum > 0 ? "+" : ""}{rub(a.manual_adjustments_sum)}
                    </td>
                    <td className="num tabular strong" style={{ color: "var(--ink-1)" }}>{rub(a.total_accrued)}</td>
                    <td className="num tabular" style={{ color: "var(--green)" }}>{rub(a.total_paid)}</td>
                    <td className="num tabular strong" style={{ color: a.balance > 0 ? "#a85f12" : "var(--green)" }}>
                      {a.balance > 0 ? rub(a.balance) : <Icon name="check" size={12}/>}
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                      <button className="btn btn-sm" title="Отправить сотруднику в чат (PDF)"
                              style={{ marginRight: 4 }}
                              onClick={async () => {
                                if (!confirm(`Отправить ведомость ${a.user_name} за ${fmtYM(a.ym)} в чат сотруднику?`)) return;
                                try {
                                  const st = await createPayrollStatement({ accrual_id: a.id } as any);
                                  alert(`Ведомость №${st.id} отправлена в чат сотруднику. Сумма: ${formatRub(st.total_amount)}.`);
                                } catch (e) {
                                  // detail лежит в body ApiError; message = «HTTP 409»
                                  const api = e as { status?: number; body?: { detail?: string }; message: string };
                                  const detail = api.body?.detail || api.message;
                                  if (String(detail).includes("already_exists") || api.status === 409) {
                                    alert("Эта ведомость уже была отправлена сотруднику ранее — повторная отправка не нужна. Статус согласования виден в блоке «Ведомости на согласование».");
                                  } else {
                                    alert("Ошибка отправки: " + detail);
                                  }
                                }
                              }}>
                        <Icon name="send" size={12}/>
                      </button>
                      <button className="btn btn-sm" title="Удалить" style={{ color: "var(--brick)" }}
                              onClick={async () => {
                                if (!confirm(`Удалить ведомость ${a.user_name} за ${fmtYM(a.ym)}?`)) return;
                                try { await deleteAccrual(a.id); listQ.refetch(); }
                                catch (e) { alert("Ошибка: " + (e as Error).message); }
                              }}>
                        <Icon name="trash" size={12}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId !== null && (
        <AccrualDetailModal id={openId} onClose={() => setOpenId(null)}
                            onChanged={() => listQ.refetch()}/>
      )}
      {closeOpen && (
        <CloseMonthModal onClose={() => setCloseOpen(false)}
                         onCreated={() => { setCloseOpen(false); listQ.refetch(); }}
                         defaultYm={to ? to.slice(0, 7) : currentYm()}/>
      )}
    </FinanceLayout>
  );
}

/* ── KPI плитка ─────────────────────────────────────────────── */
function Kpi({ label, value, color, strong, sub, hideIfZeroOnPrint }: {
  label: string; value: string; color?: string; strong?: boolean; sub?: string;
  /** Скрывать KPI-плитку при печати, если значение нулевое. */
  hideIfZeroOnPrint?: boolean;
}) {
  return (
    <div className={hideIfZeroOnPrint ? "kpi-hide-if-zero" : undefined}
         style={{ padding: 10, borderRadius: 6, background: "var(--paper-2)" }}>
      <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div className="tabular" style={{ fontSize: strong ? 18 : 14, fontWeight: strong ? 700 : 500, marginTop: 4, color }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ── Закрыть месяц — модалка ──────────────────────────────────── */
function CloseMonthModal({ onClose, onCreated, defaultYm }: { onClose: () => void; onCreated: () => void; defaultYm: string }) {
  const [ym, setYm] = useState(defaultYm);
  const mut = useMutation({
    mutationFn: () => closeMonth({ ym }),
    onSuccess: (res) => {
      alert(`Создано ведомостей: ${res.count}`);
      onCreated();
    },
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{
            fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700,
            flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12,
          }}>Закрыть месяц</div>
          <button className="modal-close" onClick={onClose} title="Закрыть (Esc)" style={{ flexShrink: 0 }}>
            <Icon name="x" size={18}/>
          </button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, color: "var(--mute)" }}>
            Создадим draft-ведомости для всех сотрудников с командой или соло-конфигом.
            Если на (user, месяц) уже есть ведомость — пропустим её.
            После создания зайдите в каждую, проверьте цифры, утвердите.
          </div>
          <label>
            <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>Месяц</div>
            <input className="input" type="month" value={ym} onChange={e => setYm(e.target.value)}/>
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8, borderTop: "1px dotted var(--line)", marginTop: 4 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={mut.isPending}>Отмена</button>
            <button className="btn btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending}>
              {mut.isPending ? "Создаём…" : "Создать ведомости"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Детальная карточка ведомости ─────────────────────────────── */
function AccrualDetailModal({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const q = useQuery({
    queryKey: ["payroll-accrual", id],
    queryFn: () => fetchAccrual(id),
    staleTime: 30_000,
  });
  const d = q.data;
  const refresh = () => { q.refetch(); onChanged(); };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window dividend-payout-modal" style={{ maxWidth: 1100, maxHeight: "94vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header no-print">
          <div style={{
            fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700,
            flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12,
          }}>Ведомость #{id}</div>
          <div style={{ display: "flex", gap: 6 }}>
            {d && (
              <>
                <button className="btn btn-sm" onClick={() => window.print()} title="Печать">
                  <Icon name="report" size={12}/> Печать
                </button>
                {!d.locked && (
                  <button className="btn btn-sm btn-primary" onClick={async () => {
                    if (!confirm("Утвердить ведомость? Цифры будут заморожены.")) return;
                    try { await approveAccrual(id); refresh(); }
                    catch (e) { alert((e as Error).message); }
                  }}>
                    <Icon name="check" size={12}/> Утвердить
                  </button>
                )}
                {d.locked && (
                  <button className="btn btn-sm" onClick={async () => {
                    if (!confirm("Разблокировать ведомость для правки?")) return;
                    try { await unlockAccrual(id); refresh(); }
                    catch (e) { alert((e as Error).message); }
                  }}>
                    <Icon name="edit" size={12}/> Разблокировать
                  </button>
                )}
              </>
            )}
          </div>
          <button className="modal-close" onClick={onClose} title="Закрыть (Esc)" style={{ flexShrink: 0, marginLeft: 4 }}>
            <Icon name="x" size={18}/>
          </button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {q.isLoading ? <div className="muted" style={{ padding: 20 }}>Загрузка…</div>
            : q.error ? <div className="alert alert-red"><div className="alert-body">{(q.error as Error).message}</div></div>
            : d && <AccrualBody d={d} onChanged={refresh}/>}
        </div>
      </div>
    </div>
  );
}

function AccrualBody({ d, onChanged }: { d: PayrollAccrualDetail; onChanged: () => void }) {
  const snap = d.snapshot;
  const { user } = useAuth();
  // Корректировки видит только бухгалтер (owner/admin/superuser), не сам менеджер.
  const canSeeAdjustments = !!user?.is_superuser || user?.role === "owner" || user?.role === "admin";
  const [editing, setEditing] = useState(false);
  const [baseSalary, setBaseSalary] = useState(String(d.base_salary));
  const [motivation, setMotivation] = useState(String(d.motivation));
  const [note, setNote] = useState(d.note ?? "");
  const [paidAt, setPaidAt] = useState(d.paid_at ?? "");

  const saveMut = useMutation({
    mutationFn: () => patchAccrual(d.id, {
      base_salary: Number(baseSalary) || 0,
      motivation: Number(motivation) || 0,
      advance_bonus: 0,
      note: note.trim() || null,
      paid_at: paidAt || null,
    }),
    onSuccess: () => { setEditing(false); onChanged(); },
    onError: (e: Error) => alert((e as Error).message),
  });
  function startEdit() {
    setBaseSalary(String(d.base_salary));
    setMotivation(String(d.motivation));
    setNote(d.note ?? ""); setPaidAt(d.paid_at ?? "");
    setEditing(true);
  }

  return (
    <>
      {/* Шапка */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Avatar name={d.user_name || ""} userId={d.user_id} avatarFilename={d.avatar_filename} size={48} color="#4fae4e"/>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{d.user_name}</div>
          <div style={{ fontSize: 12, color: "var(--mute)" }}>
            {d.position || "—"}
            {d.team_name && ` · команда «${d.team_name}»`}
            {snap.team && ` · доля ${snap.team.my_split_pct.toFixed(1)}%`}
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <span className="badge" style={{
            background: STATUS_LABELS[d.status]?.bg, color: STATUS_LABELS[d.status]?.fg,
            padding: "4px 12px", fontSize: 12,
          }}>{STATUS_LABELS[d.status]?.label}</span>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)" }}>{fmtYM(d.ym)}</div>
        </div>
      </div>

      {/* Итоги */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <Kpi label="Фикс. оклад" value={rub(d.base_salary)} color="var(--ink-1)"/>
        <Kpi label="Мотивация" value={rub(d.motivation)} color="var(--green)"/>
        {canSeeAdjustments && (
          <Kpi label="Корректировки" value={(d.manual_adjustments_sum > 0 ? "+" : "") + rub(d.manual_adjustments_sum)}
               color={d.manual_adjustments_sum < 0 ? "var(--brick)" : "var(--green)"}
               hideIfZeroOnPrint={d.manual_adjustments_sum === 0}/>
        )}
        <Kpi label="ВСЕГО НАЧИСЛЕНО" value={rub(d.total_accrued)} strong/>
        <Kpi label="Выплачено" value={rub(d.total_paid)} color="var(--green)"
             hideIfZeroOnPrint={d.total_paid === 0}/>
        <Kpi label="К выплате" value={d.balance > 0 ? rub(d.balance) : "Полностью"}
             color={d.balance > 0 ? "#a85f12" : "var(--green)"} strong/>
      </div>

      {/* Расчёт мотивации */}
      <div className="dividend-section">
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--line)" }}>
          Расчёт мотивации (на момент {fmtDate(d.created_at)})
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 13 }}>
          <div>План месяца (по договорам):</div><div className="num tabular strong">{rub(snap.parameters.month_plan)}</div>
          <div>Сумма договоров команды (для % тира):</div><div className="num tabular" style={{ color: "var(--brick)" }}>{rub((snap.parameters as { team_contracts_total?: number }).team_contracts_total ?? snap.parameters.team_revenues_total)}</div>
          <div>Приходы команды (база мотивации):</div><div className="num tabular" style={{ color: "var(--green)" }}>{rub(snap.parameters.team_revenues_total)}</div>
          <div>Выполнение плана (договоры / план):</div><div className="num tabular strong">{snap.parameters.completion_pct.toFixed(1)}%</div>
          <div>Применённый тир:</div>
          <div className="tabular">{snap.parameters.tier_rate_pct.toFixed(3)}% {snap.parameters.is_motivation_override && <span style={{ color: "#a85f12" }}>(override)</span>}<span style={{ color: "var(--mute)", marginLeft: 6 }}>«{snap.parameters.tier_label}»</span></div>
          <div>Командная мотивация (приходы × тир):</div><div className="num tabular">{rub(snap.motivation.team_motivation_total)}</div>
          <div>Доля менеджера от командной:</div><div className="num tabular">{snap.parameters.my_split_pct.toFixed(1)}% = <b>{rub(snap.motivation.my_team_motivation)}</b></div>
          {snap.motivation.personal_motivation > 0 && (<>
            <div>Мотивация от личных сделок:</div><div className="num tabular strong">{rub(snap.motivation.personal_motivation)}</div>
          </>)}
          <div style={{ borderTop: "1px solid var(--line)", paddingTop: 6, fontWeight: 700 }}>Мотивация ИТОГО:</div>
          <div className="num tabular strong" style={{ borderTop: "1px solid var(--line)", paddingTop: 6, color: "var(--green)" }}>{rub(snap.motivation.total_motivation)}</div>
        </div>
      </div>

      {/* Договоры месяца */}
      {(snap.contracts_team.length > 0 || snap.contracts_personal.length > 0) && (
        <div className="dividend-section">
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--line)" }}>
            Договоры месяца ({snap.contracts_team.length + snap.contracts_personal.length})
          </div>
          <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
            <thead><tr><th style={{ width: 90 }}>Дата</th><th>Клиент</th><th>Тип</th><th className="num" style={{ width: 120 }}>Сумма</th></tr></thead>
            <tbody>
              {snap.contracts_team.map(c => {
                const sus = (c as { date_suspicious?: boolean }).date_suspicious;
                return (
                  <tr key={`t-${c.object_id}`}>
                    <td className="tabular">
                      {fmtDate(c.signed_at)}
                      {sus && <span title="Дата подписания подозрительная (нет в карточке или позже завершения). Проверьте контракт."
                                     style={{ marginLeft: 4, color: "#a85f12", display: "inline-flex", verticalAlign: "middle" }}><Icon name="warn" size={11}/></span>}
                    </td>
                    <td>{c.client_name}</td>
                    <td><span className="badge">Командный</span></td>
                    <td className="num tabular">{rub(c.contract_sum)}</td>
                  </tr>
                );
              })}
              {snap.contracts_personal.map(c => {
                const sus = (c as { date_suspicious?: boolean }).date_suspicious;
                return (
                  <tr key={`p-${c.object_id}`}>
                    <td className="tabular">
                      {fmtDate(c.signed_at)}
                      {sus && <span title="Дата подписания подозрительная — проверьте контракт"
                                     style={{ marginLeft: 4, color: "#a85f12", display: "inline-flex", verticalAlign: "middle" }}><Icon name="warn" size={11}/></span>}
                    </td>
                    <td>{c.client_name}</td>
                    <td><span className="badge" style={{ background: "rgba(232,200,80,0.30)", color: "#a85f12" }}>
                      Личный{c.personal_motivation_pct != null && ` · ${c.personal_motivation_pct}%`}
                    </span></td>
                    <td className="num tabular">{rub(c.contract_sum)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Приходы месяца */}
      {(snap.revenues_team_pool.length > 0 || snap.revenues_personal.length > 0) && (
        <div className="dividend-section">
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--line)" }}>
            Приходы месяца ({snap.revenues_team_pool.length + snap.revenues_personal.length})
          </div>
          <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
            <thead><tr><th style={{ width: 90 }}>Дата</th><th>Объект</th><th>Контрагент</th><th>Тип</th><th className="num" style={{ width: 110 }}>Сумма</th></tr></thead>
            <tbody>
              {snap.revenues_team_pool.map(r => (
                <tr key={`tr-${r.id}`}>
                  <td className="tabular">{fmtDate(r.received_at)}</td>
                  <td>{r.object_name}</td>
                  <td>{r.counterparty || <span className="muted">—</span>}</td>
                  <td><span className="badge">Командный</span></td>
                  <td className="num tabular" style={{ color: "var(--green)" }}>{rub(r.amount)}</td>
                </tr>
              ))}
              {snap.revenues_personal.map(r => (
                <tr key={`pr-${r.id}`}>
                  <td className="tabular">{fmtDate(r.received_at)}</td>
                  <td>{r.object_name}</td>
                  <td>{r.counterparty || <span className="muted">—</span>}</td>
                  <td><span className="badge" style={{ background: "rgba(232,200,80,0.30)", color: "#a85f12" }}>
                    Личный{r.personal_motivation_pct != null && ` · ${r.personal_motivation_pct}%`}
                  </span></td>
                  <td className="num tabular" style={{ color: "var(--green)" }}>{rub(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Применённые тиры */}
      <div className="dividend-section">
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--line)" }}>
          Регламент мотивации (на {fmtDate(snap.rules.rule_applied_at)})
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
          {snap.rules.tiers.map((t, i) => (
            <div key={i} style={{
              padding: "6px 10px", borderRadius: 6,
              background: snap.parameters.tier_rate === t.pct ? "rgba(247,224,138,0.5)" : "var(--paper-2)",
              border: snap.parameters.tier_rate === t.pct ? "1px solid #d4a73a" : "1px solid var(--line)",
            }}>
              <div style={{ color: "var(--mute)" }}>{t.label || `${t.min_pct}-${t.max_pct}%`}</div>
              <div className="tabular strong">{(t.pct * 100).toFixed(3)}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Корректировки (только бухгалтеру/собственнику; при печати если 0 — скрыть) */}
      {canSeeAdjustments && (
        <div className={d.adjustments.length === 0 ? "no-print" : undefined}>
          <AdjustmentsBlock accrual={d} onChanged={onChanged}/>
        </div>
      )}

      {/* Выплаты */}
      <div className="dividend-section">
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--line)" }}>
          Привязанные выплаты ({d.paid_items.length})
        </div>
        {d.paid_items.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--mute)", padding: 8 }}>
            Выплат ещё не было. Привяжите расход (kind=fot) к этой ведомости из «Журнала расходов» / «ДДС» —
            появится здесь автоматически. На странице «Журнал расходов» в форме расхода будет выбор ведомости.
          </div>
        ) : (
          <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
            <thead><tr><th style={{ width: 90 }}>Дата</th><th>Описание</th><th>Способ</th><th className="num" style={{ width: 110 }}>Сумма</th></tr></thead>
            <tbody>
              {d.paid_items.map(p => (
                <tr key={p.id}>
                  <td className="tabular">{fmtDate(p.date)}</td>
                  <td>{p.description || p.category_name || <span className="muted">—</span>}</td>
                  <td>{p.payment_method || "—"}</td>
                  <td className="num tabular" style={{ color: "var(--green)" }}>{rub(p.amount)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                <td colSpan={3}>Итого выплачено</td>
                <td className="num tabular" style={{ color: "var(--green)" }}>{rub(d.total_paid)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Правка цифр (только если не locked) */}
      <div className="dividend-section no-print">
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--line)" }}>
          Правка
        </div>
        {!editing ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn btn-sm" onClick={startEdit} disabled={d.locked}>
              <Icon name="edit" size={12}/> Изменить оклад / мотивацию / заметку
            </button>
            {d.locked && <span style={{ color: "var(--mute)", fontSize: 12 }}>Ведомость заблокирована — сначала «Разблокировать» наверху.</span>}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Фикс. оклад</div>
              <input className="input" type="number" value={baseSalary} onChange={e => setBaseSalary(e.target.value)}/></label>
            <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Мотивация</div>
              <input className="input" type="number" value={motivation} onChange={e => setMotivation(e.target.value)}/></label>
            <label><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Дата выплаты</div>
              <input className="input" type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)}/></label>
            <label style={{ gridColumn: "1 / -1" }}><div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Заметка</div>
              <input className="input" value={note} onChange={e => setNote(e.target.value)}/></label>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost" onClick={() => setEditing(false)} disabled={saveMut.isPending}>Отмена</button>
              <button className="btn btn-primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                {saveMut.isPending ? "Сохранение…" : "Сохранить"}
              </button>
            </div>
          </div>
        )}
      </div>

      {d.note && <div style={{ fontSize: 13, color: "var(--mute)", display: "flex", alignItems: "center", gap: 6 }}><Icon name="edit" size={12}/>{d.note}</div>}
    </>
  );
}

function AdjustmentRow({ a, onChanged }: { a: PayrollAccrualDetail["adjustments"][number]; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<PayrollAdjustmentKind>(a.kind);
  const [amount, setAmount] = useState(String(a.amount));
  const [comment, setComment] = useState(a.comment || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const num = parseFloat(amount.replace(",", "."));
    if (!isFinite(num)) { alert("Введите число"); return; }
    setSaving(true);
    try {
      await patchAdjustment(a.id, { kind, amount: num, comment: comment.trim() || null });
      setEditing(false);
      onChanged();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <tr style={{ background: "var(--paper-2)" }}>
        <td>
          <select className="input-sm" value={kind}
                  onChange={e => setKind(e.target.value as PayrollAdjustmentKind)}>
            <option value="bonus">Премия</option>
            <option value="penalty">Штраф</option>
            <option value="correction">Корректировка</option>
            <option value="travel">Командировка</option>
            <option value="other">Прочее</option>
          </select>
        </td>
        <td>
          <input className="input-sm" placeholder="Комментарий" value={comment}
                 onChange={e => setComment(e.target.value)} style={{ width: "100%" }}/>
        </td>
        <td colSpan={2}>
          <input className="input-sm" type="number" placeholder="Сумма (+/−)" value={amount}
                 onChange={e => setAmount(e.target.value)} style={{ width: "100%" }}/>
        </td>
        <td className="num">
          <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
            <button className="btn btn-sm btn-primary" onClick={save} disabled={saving} title="Сохранить">
              {saving ? "…" : "OK"}
            </button>
          </div>
        </td>
        <td className="no-print">
          <button className="btn btn-sm" onClick={() => {
            setEditing(false);
            setKind(a.kind); setAmount(String(a.amount)); setComment(a.comment || "");
          }} disabled={saving} title="Отмена" style={{ padding: "2px 6px" }}><Icon name="x" size={11}/></button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{ADJ_LABELS[a.kind]}</td>
      <td>{a.comment || <span className="muted">—</span>}</td>
      <td style={{ color: "var(--mute)" }}>{a.created_by_name || "—"}</td>
      <td className="tabular" style={{ color: "var(--mute)" }}>{fmtDate(a.created_at)}</td>
      <td className="num tabular strong" style={{ color: a.amount < 0 ? "var(--brick)" : "var(--green)" }}>
        {a.amount > 0 ? "+" : ""}{rub(a.amount)}
      </td>
      <td className="no-print">
        <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <button className="btn btn-sm" style={{ padding: "2px 6px" }}
                  title="Редактировать корректировку"
                  onClick={() => setEditing(true)}>✎</button>
          <button className="btn btn-sm" style={{ color: "var(--brick)", padding: "2px 6px" }}
                  title="Удалить корректировку"
                  onClick={async () => {
                    if (!confirm(`Удалить корректировку «${a.comment || ADJ_LABELS[a.kind]}»?`)) return;
                    try { await deleteAdjustment(a.id); onChanged(); }
                    catch (e) { alert((e as Error).message); }
                  }}><Icon name="trash" size={12}/></button>
        </div>
      </td>
    </tr>
  );
}


function AdjustmentsBlock({ accrual, onChanged }: { accrual: PayrollAccrualDetail; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<PayrollAdjustmentKind>("bonus");
  const [amount, setAmount] = useState("");
  const [comment, setComment] = useState("");

  // Парсинг суммы: убираем пробелы (тысячные разделители), запятая → точка.
  const parsedAmount = (() => {
    const s = (amount || "").replace(/\s+/g, "").replace(",", ".");
    const n = parseFloat(s);
    return isFinite(n) ? n : NaN;
  })();
  const isAmountValid = !isNaN(parsedAmount) && parsedAmount !== 0;

  const mut = useMutation({
    mutationFn: () => addAdjustment(accrual.id, {
      kind,
      amount: parsedAmount,
      comment: comment.trim() || null,
    }),
    onSuccess: () => { setOpen(false); setAmount(""); setComment(""); onChanged(); },
    onError: (e: Error) => alert("Ошибка: " + (e as Error).message),
  });

  const onSave = () => {
    if (!isAmountValid) {
      alert("Введи сумму (число, можно отрицательное для штрафа). Пример: 23607 или -5000.");
      return;
    }
    mut.mutate();
  };
  return (
    <div className="dividend-section">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--line)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>
          Корректировки ({accrual.adjustments.length})
        </div>
        <button className="btn btn-sm no-print" style={{ marginLeft: "auto" }} onClick={() => setOpen(v => !v)}>
          <Icon name="plus" size={11}/> Добавить
        </button>
      </div>

      {open && (
        <div className="no-print" style={{ padding: 10, background: "var(--paper-2)", borderRadius: 6, marginBottom: 8, display: "grid", gridTemplateColumns: "auto 1fr 1fr auto auto", gap: 8, alignItems: "end" }}>
          <select className="input-sm" value={kind} onChange={e => setKind(e.target.value as PayrollAdjustmentKind)}>
            <option value="bonus">Премия</option>
            <option value="penalty">Штраф</option>
            <option value="correction">Корректировка</option>
            <option value="travel">Командировка</option>
            <option value="other">Прочее</option>
          </select>
          <input className="input-sm" type="text" inputMode="decimal" placeholder="Сумма (+ или −)" value={amount}
                 onChange={e => setAmount(e.target.value)}
                 onKeyDown={e => { if (e.key === "Enter" && isAmountValid) onSave(); }}/>
          <input className="input-sm" placeholder="Комментарий" value={comment}
                 onChange={e => setComment(e.target.value)}
                 onKeyDown={e => { if (e.key === "Enter" && isAmountValid) onSave(); }}/>
          <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={mut.isPending || !isAmountValid}>
            {mut.isPending ? "…" : "OK"}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => { setOpen(false); setAmount(""); setComment(""); }}
                  title="Отмена" disabled={mut.isPending}>
            <Icon name="x" size={11}/>
          </button>
        </div>
      )}

      {accrual.adjustments.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--mute)", padding: 6 }}>
          Корректировок нет. Добавьте штраф / премию / поправку — она сразу попадёт в «Всего начислено».
        </div>
      ) : (
        <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
          <thead><tr><th style={{ width: 110 }}>Тип</th><th>Комментарий</th><th>Автор</th><th>Дата</th><th className="num" style={{ width: 110 }}>Сумма</th><th style={{ width: 30 }}/></tr></thead>
          <tbody>
            {accrual.adjustments.map(a => (
              <AdjustmentRow key={a.id} a={a} onChanged={onChanged} />
            ))}
            <tr style={{ fontWeight: 700, borderTop: "2px solid var(--line)" }}>
              <td colSpan={4}>Итого корректировок</td>
              <td className="num tabular" style={{ color: accrual.manual_adjustments_sum < 0 ? "var(--brick)" : "var(--green)" }}>
                {accrual.manual_adjustments_sum > 0 ? "+" : ""}{rub(accrual.manual_adjustments_sum)}
              </td>
              <td/>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
